"use client";

// French copy of components/widgetNavbar/widgetNavbar.comp.tsx (FR-1).
// Hub-only: no platform spokes, no coming-soon state (all launch widgets are
// published). Templates and How-to-add pages exist only in English, so those
// tabs link to the English pages and carry an EN tag.
import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useInView } from "react-intersection-observer";
import {
  StyledFloatingButton,
  StyledWidgetNav,
} from "../widgetNavbar/widgetNavbar.styles";
import { IPlugin } from "../../types";
import { CustomLink } from "../link/link.comp";
import { trackEvent } from "@/lib/track";
import { createMobileAwareClickHandler } from "../../helpers/mobile-redirect";
import { frHref, hasFrTwin } from "@/i18n/fr/routes";
import { EnTag } from "./langSwitch.comp";

interface IWidgetNavbarFrProps {
  widget: IPlugin;
  showTemplatesTab?: boolean;
}

export const WidgetNavbarFr = ({ widget, showTemplatesTab }: IWidgetNavbarFrProps) => {
  const [loaded, setLoaded] = useState(false);
  const { ref, inView } = useInView({ threshold: 0 });
  const [shake, setShake] = useState(false);
  const [playgroundPassed, setPlaygroundPassed] = useState(true);

  const widgetSlug = widget.seoSlug || widget.slug;
  const overviewPath = frHref(`/widgets/${widgetSlug}`);
  const editorPath = `/${widget.slug}/editor`;

  useEffect(() => {
    setLoaded(true);
    const interval = setInterval(() => {
      setShake(true);
      setTimeout(() => setShake(false), 1000);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Same rule as the English navbar: the floating CTA waits until the visitor
  // has scrolled past the live editor playground, when the page has one.
  useEffect(() => {
    const playground = document.querySelector("[data-cn-live-playground]");
    if (!playground) return;
    setPlaygroundPassed(false);
    const observer = new IntersectionObserver(
      ([entry]) =>
        setPlaygroundPassed(
          !entry.isIntersecting && entry.boundingClientRect.bottom < 0
        ),
      { threshold: 0 }
    );
    observer.observe(playground);
    return () => observer.disconnect();
  }, []);

  function renderNavContent(isFixed?: boolean) {
    return (
      <div className="links">
        {isFixed !== undefined && (
          <Link href={overviewPath} className="logo">
            <Image src={widget.logo} alt={`Logo\u00a0: ${widget.name}`} width={30} height={30} />
            {widget.name}
          </Link>
        )}
        <ul>
          <li>
            <Link className="active" href={overviewPath}>
              Présentation
            </Link>
          </li>
          {showTemplatesTab !== false && (
            <li className="templates">
              <Link href={`/widgets/${widgetSlug}/templates`} hrefLang="en">
                Modèles
                <EnTag />
              </Link>
            </li>
          )}
          <li className="how-to-add">
            <Link href={`/widgets/${widgetSlug}/how-to-add`} hrefLang="en">
              Intégration
              <EnTag />
            </Link>
          </li>
          {!inView && (
            <>
              <li>
                <Link href={frHref("/pricing")}>
                  Tarifs
                  {!hasFrTwin("/pricing") && <EnTag />}
                </Link>
              </li>
              <li className="cta-li">
                <CustomLink
                  external
                  title={`Essai gratuit\u00a0: ${widget.name}`}
                  href={editorPath}
                  onClick={createMobileAwareClickHandler(() => {}, editorPath)}
                  id="menu-try-cta"
                >
                  Essai gratuit
                </CustomLink>
              </li>
            </>
          )}
        </ul>
      </div>
    );
  }

  return (
    <>
      <StyledWidgetNav ref={ref} className={`${inView || !loaded ? "show" : ""}`}>
        {renderNavContent(false)}
      </StyledWidgetNav>
      <StyledWidgetNav className={`fixed ${!inView && loaded ? "show" : ""}`}>
        {renderNavContent(true)}
      </StyledWidgetNav>
      <StyledFloatingButton
        className={`floating-button ${shake ? "shake" : ""}`}
        style={{ display: !inView && loaded && playgroundPassed ? "block" : "none" }}
        href={editorPath}
        onClick={createMobileAwareClickHandler(() => {
          trackEvent(`Floating try for free`, {
            serviceName: widget.serviceName,
            locale: "fr",
          });
        }, editorPath)}
        id="floating-try-cta"
      >
        Essayez gratuitement !
      </StyledFloatingButton>
    </>
  );
};
